import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TokenService } from '@app/auth/auth/token.service';
import { ProductoList } from '@app/entidades/operacion.model';
import { AdminService } from '@app/servicios/admin/admin.service';
import { SearchService } from '@app/servicios/search/search.service';
import { StorageService } from '@app/servicios/storage/storage.service';
import { environment } from '@env';
import { Subject, Subscription, debounceTime, distinctUntilChanged, filter } from 'rxjs';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  fotoUrl = environment.servImgURL + '/';

  unidadesSaphirus = 0;

  public carrito = true;
  public login = false;
  public loggedIn = false;
  public userName: string = '';
  public subscriber!: Subscription;

  public buscado: string = '';
  private searchSubject = new Subject<string>();

  public cart = false;
  public lista: ProductoList[] = [];
  public listaFiltrada: ProductoList[] = [];
  public filtroCarrito: string = '';
  public total = 0;

  mobile: boolean = false;
  web: boolean = false;
  subtotal: number = 0;

  @HostListener('window:resize', ['$event'])
  onResize() {
    const width = window.innerWidth;
    this.mobile = width < 1024;
    this.web = width >= 1024;
  }

  constructor(
    private tokenService: TokenService,
    private storageService: StorageService,
    private router: Router,
    private adminService: AdminService,
    private searchService: SearchService
  ) {}

  ngOnInit() {
    this.onResize();
    this.verificarSesion();

    this.subscriber = new Subscription();

    this.subscriber.add(
      this.searchService.searchQuery$.subscribe((term) => {
        this.buscado = term;
      })
    );

    this.subscriber.add(
      this.searchSubject
        .pipe(debounceTime(350), distinctUntilChanged())
        .subscribe((term) => {
          this.ejecutarBusqueda(term);
        })
    );

    this.subscriber.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.verificarSesion();
          const url = event.urlAfterRedirects || event.url;
          this.carrito = !url.includes('finalizar') && !url.includes('login');
          this.login = url.includes('login');
        })
    );
  }

  onSearchChange(term: string) {
    this.searchSubject.next(term);
  }

  ejecutarBusqueda(term: string = this.buscado) {
    this.searchService.setSearchQuery(term);
    const currentUrl = this.router.url;

    if (this.loggedIn || currentUrl.includes('espacio-mayorista')) {
      if (!currentUrl.includes('/espacio-mayorista/tienda2')) {
        this.router.navigate(['/espacio-mayorista/tienda2', 'all', 'all', 'all', 0]);
      }
    } else {
      if (!currentUrl.includes('/tienda')) {
        this.router.navigate(['/tienda', 'all', 'all', 'all', 0]);
      }
    }
  }

  limpiarBuscado() {
    this.buscado = '';
    this.searchService.clearSearch();
  }

  public verificarSesion() {
    if (this.tokenService.isLoggedIn() && this.tokenService.notExpired()) {
      this.userName = this.storageService.getItem('userT') || '';
      this.loggedIn = true;
    } else {
      this.loggedIn = false;
      this.userName = '';
    }
  }

  logOut() {
    this.tokenService.logOut();
    this.loggedIn = false;
    this.verificarSesion();
    this.router.navigate(['/tienda', 'all', 'all', 'all', 0]);
  }

  get totalItems(): number {
    const list = this.storageService.getItem('lp');
    if (!list || !Array.isArray(list)) return 0;
    return list.reduce((acc: number, item: any) => acc + (+(item.cantidad ?? 1)), 0);
  }

  /*--- CARRITO ---*/
  consultarProductos() {
    this.lista = this.storageService.getItem('lp') || [];

    if (!this.lista || this.lista.length === 0) {
      this.total = 0;
      this.subtotal = 0;
      this.listaFiltrada = [];
      return;
    }

    this.checkearSaphirus();

    if (this.unidadesSaphirus >= 600) {
      this.precioSaphirus();
    } else {
      this.calcularTotal();
    }

    this.filtrarCarrito(this.filtroCarrito);
  }

  calcularTotal() {
    if (!this.lista || this.lista.length === 0) {
      this.total = 0;
      this.subtotal = 0;
      return;
    }

    if (this.loggedIn) {
      let subtotal = 0;
      this.lista.forEach((producto) => {
        const cant = +(producto.cantidad ?? 0);
        subtotal += (+(producto.precio?.may1 ?? 0)) * cant;
      });

      this.subtotal = subtotal;
      const montoMay2 = this.adminService.current_monto || environment.montoMay2 || 200000;

      if (subtotal >= montoMay2) {
        let total = 0;
        this.lista.forEach((producto) => {
          const cant = +(producto.cantidad ?? 0);
          total += (+(producto.precio?.may2 ?? producto.precio?.may1 ?? 0)) * cant;
        });
        this.total = total;
      } else {
        this.total = subtotal;
      }
    } else {
      let total = 0;
      this.lista.forEach((producto) => {
        const cant = +(producto.cantidad ?? 0);
        total += (+(producto.precio?.publico ?? 0)) * cant;
      });

      this.total = total;
      this.subtotal = total;
    }
  }

  sumar(id: string) {
    if (!this.lista) return;

    this.lista.forEach((producto) => {
      if (producto.id === id) {
        const maxStock = +(producto.stock ?? 99999);
        if (producto.cantidad < maxStock) {
          producto.cantidad++;
        }
      }
    });
    this.storageService.setItem('lp', this.lista);
    this.checkearSaphirus();

    if (this.unidadesSaphirus >= 600) {
      this.precioSaphirus();
    } else {
      this.calcularTotal();
    }
    this.filtrarCarrito(this.filtroCarrito);
  }

  restar(id: string) {
    if (!this.lista) return;

    this.lista.forEach((producto) => {
      if (producto.id === id) {
        if (producto.cantidad > 1) {
          producto.cantidad--;
        }
      }
    });
    this.storageService.setItem('lp', this.lista);
    this.checkearSaphirus();

    if (this.unidadesSaphirus >= 600) {
      this.precioSaphirus();
    } else {
      this.calcularTotal();
    }
    this.filtrarCarrito(this.filtroCarrito);
  }

  public eliminar(id: any) {
    if (!this.lista) return;

    this.lista = this.lista.filter((p) => p.id !== id);

    this.checkearSaphirus();

    if (this.unidadesSaphirus >= 600) {
      this.precioSaphirus();
    } else {
      this.calcularTotal();
    }

    this.storageService.setItem('lp', this.lista);
    this.filtrarCarrito(this.filtroCarrito);
  }

  vaciarCarrito(): void {
    this.lista = [];
    this.listaFiltrada = [];
    this.storageService.removeItem('lp');
    this.total = 0;
    this.subtotal = 0;
    this.unidadesSaphirus = 0;
  }

  filtrarCarrito(term: string = ''): void {
    this.filtroCarrito = term;
    if (!this.lista) {
      this.listaFiltrada = [];
      return;
    }
    if (!term || !term.trim()) {
      this.listaFiltrada = [...this.lista];
      return;
    }
    const normalizado = term.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    this.listaFiltrada = this.lista.filter((p) => {
      const nombreNorm = (p.nombre || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const marcaNorm = (p.marca || p.tipo || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const descNorm = (p.descripcion || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return nombreNorm.includes(normalizado) || marcaNorm.includes(normalizado) || descNorm.includes(normalizado);
    });
  }

  limpiarFiltroCarrito(): void {
    this.filtroCarrito = '';
    this.listaFiltrada = [...(this.lista || [])];
  }

  get montoMay2Threshold(): number {
    return this.adminService.current_monto || environment.montoMay2 || 200000;
  }

  get aplicaMay2(): boolean {
    if (!this.loggedIn) return false;
    return (this.subtotal ?? 0) >= this.montoMay2Threshold;
  }

  getPrecioUnitario(prod: ProductoList): number {
    if (!prod || !prod.precio) return 0;
    if (prod.tipo === 'SAPHIRUS' && this.unidadesSaphirus >= 600) {
      return Number(prod.precio.may3 ?? 0);
    }
    if (this.loggedIn) {
      if (this.aplicaMay2) {
        return Number(prod.precio.may2 ?? prod.precio.may1 ?? 0);
      }
      return Number(prod.precio.may1 ?? 0);
    }
    return Number(prod.precio.publico ?? 0);
  }

  getPrecioTotal(prod: ProductoList): number {
    return this.getPrecioUnitario(prod) * (Number(prod.cantidad) || 0);
  }

  checkearSaphirus() {
    this.unidadesSaphirus = 0;
    this.lista?.forEach((pp) => {
      if ((pp.tipo ?? '').toString().toUpperCase() === 'SAPHIRUS') {
        this.unidadesSaphirus += Number(pp.cantidad ?? 0);
      }
    });
  }

  precioSaphirus() {
    if (!this.lista || this.lista.length === 0) {
      this.total = 0;
      this.subtotal = 0;
      return;
    }

    if (this.loggedIn) {
      let subtotal = 0;
      this.lista.forEach((producto) => {
        const cant = +(producto.cantidad ?? 0);
        if ((producto.tipo ?? '').toString().toUpperCase() === 'SAPHIRUS') {
          subtotal += (+(producto.precio?.may3 ?? 0)) * cant;
        } else {
          subtotal += (+(producto.precio?.may1 ?? 0)) * cant;
        }
      });
      this.subtotal = subtotal;

      const montoMay2 = this.adminService.current_monto || environment.montoMay2 || 200000;
      if (subtotal >= montoMay2) {
        let total = 0;
        this.lista.forEach((producto) => {
          const cant = +(producto.cantidad ?? 0);
          if ((producto.tipo ?? '').toString().toUpperCase() === 'SAPHIRUS') {
            total += (+(producto.precio?.may3 ?? 0)) * cant;
          } else {
            total += (+(producto.precio?.may2 ?? producto.precio?.may1 ?? 0)) * cant;
          }
        });
        this.total = total;
      } else {
        this.total = subtotal;
      }
    } else {
      let total = 0;
      this.lista.forEach((producto) => {
        const cant = +(producto.cantidad ?? 0);
        if ((producto.tipo ?? '').toString().toUpperCase() === 'SAPHIRUS') {
          total += (+(producto.precio?.may3 ?? 0)) * cant;
        } else {
          total += (+(producto.precio?.publico ?? 0)) * cant;
        }
      });

      this.total = total;
      this.subtotal = total;
    }
  }

  ngOnDestroy() {
    if (this.subscriber) {
      this.subscriber.unsubscribe();
    }
  }
}
